import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Plus, Sparkles, Eye, X } from 'lucide-react';
import api from '@/lib/api';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Textarea from '@/components/ui/Textarea';
import Select from '@/components/ui/Select';
import Card from '@/components/ui/Card';
import Modal from '@/components/ui/Modal';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import TipTapEditor from '@/components/admin/TipTapEditor';
import AiBlogGenerator, { type AiGeneratedData } from '@/components/admin/AiBlogGenerator';
import AiForgeButton from '@/components/admin/AiForgeButton';
import ImageUploader from '@/components/admin/ImageUploader';

interface BlogCategory {
  id: string;
  name: string;
  slug: string;
}

interface BlogPostData {
  id?: string;
  title: string;
  slug: string;
  content: string;
  excerpt: string;
  status: 'draft' | 'published';
  category_id: string;
  published_at: string;
  featured_image_url: string;
  tags: string;
  meta_title: string;
  meta_description: string;
  ai_text_model?: string;
  ai_image_model?: string;
}

export default function BlogEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNewPost = id === 'new';

  const [loading, setLoading] = useState(!isNewPost);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategorySlug, setNewCategorySlug] = useState('');
  const [savingCategory, setSavingCategory] = useState(false);
  const [categoryError, setCategoryError] = useState('');
  const [imagePrompt, setImagePrompt] = useState('');
  const [showImageStudio, setShowImageStudio] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const initialFormData = useRef<BlogPostData | null>(null);
  const [formData, setFormData] = useState<BlogPostData>({
    title: '',
    slug: '',
    content: '',
    excerpt: '',
    status: 'draft',
    category_id: '',
    published_at: new Date().toISOString().slice(0, 16),
    featured_image_url: '',
    tags: '',
    meta_title: '',
    meta_description: '',
  });

  const isDirty = useCallback(() => {
    if (!initialFormData.current) return false;
    const initial = initialFormData.current;
    return (Object.keys(initial) as (keyof BlogPostData)[]).some(
      key => formData[key] !== initial[key]
    );
  }, [formData]);

  // Snapshot initial form data once loaded
  useEffect(() => {
    if (!loading && initialFormData.current === null) {
      initialFormData.current = { ...formData };
    }
  }, [loading, formData]);

  useEffect(() => {
    fetchCategories();
    if (!isNewPost && id) {
      fetchPost(id);
    }
  }, [id, isNewPost]);

  const fetchCategories = async () => {
    try {
      const response = await api.get<{ categories: BlogCategory[] }>('/admin/blog/categories');
      setCategories(response.categories);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const fetchPost = async (postId: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get<any>(`/admin/blog/${postId}`);
      setFormData({
        ...response,
        status: response.is_published ? 'published' : 'draft',
        published_at: response.published_at
          ? new Date(response.published_at).toISOString().slice(0, 16)
          : new Date().toISOString().slice(0, 16),
      });
    } catch (err) {
      setError('Failed to load blog post. Please try again.');
      console.error('Error fetching post:', err);
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (title: string): string => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleTitleChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      title: value,
      slug: prev.slug === '' || prev.slug === generateSlug(prev.title)
        ? generateSlug(value)
        : prev.slug
    }));
  };

  const handleAiGenerated = (data: AiGeneratedData) => {
    setFormData(prev => ({
      ...prev,
      title: data.title,
      slug: generateSlug(data.title),
      content: data.content,
      excerpt: data.excerpt,
      tags: data.tags,
      meta_title: data.meta_title,
      meta_description: data.meta_description,
      featured_image_url: data.featured_image_url || prev.featured_image_url,
      ai_text_model: data.ai_text_model || prev.ai_text_model,
      ai_image_model: data.ai_image_model || prev.ai_image_model,
    }));
    if (data.image_prompt) setImagePrompt(data.image_prompt);
  };

  const validateForm = (): Record<string, string> => {
    const errors: Record<string, string> = {};
    if (!formData.title.trim()) errors.title = 'Title is required';
    if (!formData.slug.trim()) errors.slug = 'Slug is required';
    if (!formData.content.trim() || formData.content === '<p></p>') errors.content = 'Content is required';
    if (!formData.category_id) errors.category_id = 'Please select a category';
    return errors;
  };

  const generateCategorySlug = (name: string): string => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleNewCategoryNameChange = (value: string) => {
    setNewCategoryName(value);
    if (newCategorySlug === '' || newCategorySlug === generateCategorySlug(newCategoryName)) {
      setNewCategorySlug(generateCategorySlug(value));
    }
  };

  const handleSaveCategory = async () => {
    if (!newCategoryName.trim() || !newCategorySlug.trim()) return;

    try {
      setSavingCategory(true);
      setCategoryError('');
      const newCat = await api.post<BlogCategory>('/admin/blog/categories', {
        name: newCategoryName.trim(),
        slug: newCategorySlug.trim(),
      });
      setCategories(prev => [...prev, { id: String(newCat.id), name: newCat.name, slug: newCat.slug }]);
      setFormData(prev => ({ ...prev, category_id: String(newCat.id) }));
      setFieldErrors(prev => ({ ...prev, category_id: '' }));
      setShowCategoryModal(false);
      setNewCategoryName('');
      setNewCategorySlug('');
    } catch (err: any) {
      setCategoryError(err.message || 'Failed to create category');
    } finally {
      setSavingCategory(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setFieldErrors({});

      const { status, ...rest } = formData;
      const payload = {
        ...rest,
        is_published: status === 'published',
        published_at: status === 'published' ? formData.published_at : null,
      };

      if (isNewPost) {
        const response = await api.post<{ id: string }>('/admin/blog', payload);
        navigate(`/admin/blog/${response.id}`);
      } else {
        await api.put(`/admin/blog/${id}`, payload);
        navigate('/admin/blog');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save blog post. Please try again.');
      console.error('Error saving post:', err);
    } finally {
      setSaving(false);
    }
  };

  const categoryOptions = [
    { label: 'Select a category', value: '' },
    ...categories.map(cat => ({ label: cat.name, value: cat.id }))
  ];

  const statusOptions = [
    { label: 'Draft', value: 'draft' },
    { label: 'Published', value: 'published' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" role="status" aria-live="polite">
        <LoadingSpinner />
        <span className="sr-only">Loading blog post...</span>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/admin/blog')}
          className="mb-4"
          aria-label="Back to blog posts"
        >
          <ArrowLeft className="w-4 h-4 mr-2" aria-hidden="true" />
          Back to Blog
        </Button>
        <h1 className="text-3xl font-bold text-gray-900">
          {isNewPost ? 'Create New Post' : 'Edit Post'}
        </h1>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg" role="alert">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      <form ref={formRef} onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {isNewPost && <AiBlogGenerator onGenerated={handleAiGenerated} />}

            <Card padding="lg">
              <div className="space-y-6">
                <Input
                  label="Post Title"
                  value={formData.title}
                  onChange={(e) => {
                    handleTitleChange(e.target.value);
                    setFieldErrors(prev => ({ ...prev, title: '' }));
                  }}
                  error={fieldErrors.title}
                  required
                  aria-required="true"
                />

                <Textarea
                  label="Excerpt"
                  value={formData.excerpt}
                  onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                  rows={3}
                  helperText="Brief summary of the post (shown in listings)"
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Content <span className="text-red-500">*</span>
                  </label>
                  <TipTapEditor
                    content={formData.content}
                    onChange={(html) => {
                      setFormData(prev => ({ ...prev, content: html }));
                      setFieldErrors(prev => ({ ...prev, content: '' }));
                    }}
                    placeholder="Write your blog post content..."
                  />
                  {fieldErrors.content && (
                    <p className="mt-1 text-sm text-red-500" role="alert">{fieldErrors.content}</p>
                  )}
                </div>
              </div>
            </Card>

            <Card title="SEO Settings" padding="lg">
              <div className="space-y-6">
                <Input
                  label="Slug"
                  value={formData.slug}
                  onChange={(e) => {
                    setFormData({ ...formData, slug: e.target.value });
                    setFieldErrors(prev => ({ ...prev, slug: '' }));
                  }}
                  error={fieldErrors.slug}
                  helperText="URL-friendly version of the title"
                  required
                  aria-required="true"
                />

                <Input
                  label="Meta Title"
                  value={formData.meta_title}
                  onChange={(e) => setFormData({ ...formData, meta_title: e.target.value })}
                  helperText="Leave empty to use post title"
                />

                <Textarea
                  label="Meta Description"
                  value={formData.meta_description}
                  onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                  rows={3}
                  helperText="Brief description for search engines (150-160 characters recommended)"
                />
              </div>
            </Card>
          </div>

          <div className="space-y-6">
            <Card title="Post Settings" padding="lg">
              <div className="space-y-4">
                <Select
                  label="Status"
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as 'draft' | 'published' })}
                  options={statusOptions}
                  required
                  aria-required="true"
                />

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-gray-700">
                      Category <span className="text-red-500">*</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowCategoryModal(true)}
                      className="text-sm text-green-700 hover:text-green-800 font-medium flex items-center gap-1"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      New
                    </button>
                  </div>
                  <Select
                    value={formData.category_id}
                    onChange={(e) => {
                      setFormData({ ...formData, category_id: e.target.value });
                      setFieldErrors(prev => ({ ...prev, category_id: '' }));
                    }}
                    options={categoryOptions}
                    error={fieldErrors.category_id}
                    required
                    aria-required="true"
                  />
                </div>

                <Input
                  label="Publish Date"
                  type="datetime-local"
                  value={formData.published_at}
                  onChange={(e) => setFormData({ ...formData, published_at: e.target.value })}
                  required
                  aria-required="true"
                />
              </div>
            </Card>

            <Card title="Featured Image" padding="lg">
              <ImageUploader
                value={formData.featured_image_url}
                onChange={(url) => setFormData(prev => ({ ...prev, featured_image_url: url }))}
                label="Featured Image"
                targetWidth={1200}
                targetHeight={630}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-3 w-full"
                onClick={() => setShowImageStudio(true)}
              >
                <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                Generate with AI
              </Button>
            </Card>

            <Card title="Tags" padding="lg">
              <Input
                label="Tags"
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                helperText="Comma-separated tags (e.g., tips, how-to, news)"
              />
            </Card>

            {(formData.ai_text_model || formData.ai_image_model) && (
              <Card title="AI Attribution" padding="lg">
                <div className="space-y-1.5 text-xs text-gray-500">
                  {formData.ai_text_model && (
                    <div className="flex items-center gap-1.5">
                      <Sparkles className="w-3 h-3 text-purple-400" />
                      <span>Text: <span className="font-mono text-gray-700">{formData.ai_text_model}</span></span>
                    </div>
                  )}
                  {formData.ai_image_model && (
                    <div className="flex items-center gap-1.5">
                      <Sparkles className="w-3 h-3 text-purple-400" />
                      <span>Image: <span className="font-mono text-gray-700">{formData.ai_image_model}</span></span>
                    </div>
                  )}
                </div>
              </Card>
            )}

            <div className="sticky top-6 space-y-3">
              <Button
                type="submit"
                variant="primary"
                size="lg"
                className="w-full"
                isLoading={saving}
                disabled={saving}
              >
                <Save className="w-4 h-4 mr-2" aria-hidden="true" />
                {saving ? 'Saving...' : 'Save Post'}
              </Button>
              {!isNewPost && formData.slug && (
                <Button
                  type="button"
                  variant="outline"
                  size="lg"
                  className="w-full"
                  onClick={() => window.open(`/blog/${formData.slug}`, '_blank')}
                >
                  <Eye className="w-4 h-4 mr-2" aria-hidden="true" />
                  Preview
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Floating save bar */}
        {isDirty() && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50">
            <div className="flex items-center gap-3 px-5 py-3 bg-amber-50 border border-amber-300 rounded-xl shadow-lg">
              <span className="text-sm font-medium text-amber-800">You have unsaved changes</span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  if (initialFormData.current) {
                    setFormData({ ...initialFormData.current });
                  }
                }}
                disabled={saving}
              >
                <X className="w-3.5 h-3.5 mr-1.5" aria-hidden="true" />
                Discard
              </Button>
              <Button
                type="submit"
                variant="primary"
                size="sm"
                isLoading={saving}
                disabled={saving}
              >
                <Save className="w-3.5 h-3.5 mr-1.5" aria-hidden="true" />
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        )}
      </form>

      <Modal
        isOpen={showCategoryModal}
        onClose={() => {
          setShowCategoryModal(false);
          setNewCategoryName('');
          setNewCategorySlug('');
          setCategoryError('');
        }}
        title="New Category"
      >
        <div className="space-y-4">
          {categoryError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg" role="alert">
              <p className="text-sm text-red-700">{categoryError}</p>
            </div>
          )}
          <Input
            label="Name"
            value={newCategoryName}
            onChange={(e) => handleNewCategoryNameChange(e.target.value)}
            required
          />
          <Input
            label="Slug"
            value={newCategorySlug}
            onChange={(e) => setNewCategorySlug(e.target.value)}
            helperText="URL-friendly version of the name"
            required
          />
          <div className="flex items-center justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowCategoryModal(false);
                setNewCategoryName('');
                setNewCategorySlug('');
                setCategoryError('');
              }}
              disabled={savingCategory}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              isLoading={savingCategory}
              disabled={savingCategory || !newCategoryName.trim()}
              onClick={handleSaveCategory}
            >
              Add Category
            </Button>
          </div>
        </div>
      </Modal>

      <AiForgeButton
        isOpen={showImageStudio}
        onClose={() => setShowImageStudio(false)}
        context="blog"
        contextName={formData.title}
        initialPrompt={imagePrompt}
        referenceImages={formData.featured_image_url ? [{ url: formData.featured_image_url }] : undefined}
        onImageGenerated={(img) => {
          setFormData(prev => ({ ...prev, featured_image_url: img.url }));
          setShowImageStudio(false);
        }}
      />
    </div>
  );
}
